import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

const DSTREZZED_TITLE_LIST_SELECTOR = '.search-drawer-title-list:not(.search-drawer-title-list--pages)';
const DSTREZZED_SEARCH_BOOTSTRAP_ID = 'dstrezzed-search-drawer-bootstrap';
const PRODUCT_COUNT_TITLE_SELECTOR = '[data-search-product-count]';
const DSTREZZED_PRODUCTS_SELECTOR = '[data-dstrezzed-drawer-products]';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement} [emptyStateCollectionsPages] - Hidden collections/pages markup for no-results fallback.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * SSR empty-state markup from the search block, used when resetting the modal.
   * @type {string | null}
   */
  #emptyStateMarkup = null;

  /**
   * Cached collections/pages markup from the Search block for no-results fallback.
   * @type {string | null}
   */
  #emptyStateCollectionsPagesMarkup = null;

  /**
   * Storefront search total heading, applied after predictive results render.
   * @type {{ term: string, label: string } | null}
   */
  #pendingProductCount = null;

  /**
   * Full-search product grid markup, applied after predictive results render.
   * @type {{ term: string, markup: string } | null}
   */
  #pendingDstrezzedProducts = null;

  /**
   * Query whose predictive results are currently in the drawer.
   * @type {string | null}
   */
  #renderedSearchTerm = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  /**
   * Whether the Search block is using the Dstrezzed drawer style.
   * @returns {boolean}
   */
  get #isDstrezzed() {
    return this.dataset.searchStyle === 'dstrezzed';
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    this.#emptyStateMarkup = this.refs.predictiveSearchResults?.innerHTML ?? null;
    this.#cacheEmptyStateCollectionsPages();

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (this.#isDstrezzed) {
      this.addEventListener('pointerdown', this.#handleTitleListDragStart, { signal });
      this.addEventListener('click', this.#handleTitleListClick, { signal, capture: true });
    }

    if (!this.#isDstrezzed && RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardownTitleListDrag();
    this.#controller.abort();
  }

  /** @type {HTMLElement | null} */
  #titleListElement = null;

  #isDraggingTitleList = false;

  #didDragTitleList = false;

  #titleListDragStartX = 0;

  #titleListDragStartScrollLeft = 0;

  /**
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  #hasHorizontalOverflow(element) {
    return element.scrollWidth > element.clientWidth + 1;
  }

  /**
   * @param {PointerEvent} event
   */
  #handleTitleListDragStart = (event) => {
    if (event.button !== 0) return;
    if (event.pointerType === 'touch') return;
    if (!(event.target instanceof Element)) return;

    const list = event.target.closest(DSTREZZED_TITLE_LIST_SELECTOR);
    if (!(list instanceof HTMLElement)) return;
    if (!this.#hasHorizontalOverflow(list)) return;

    this.#titleListElement = list;
    this.#isDraggingTitleList = true;
    this.#didDragTitleList = false;
    this.#titleListDragStartX = event.pageX;
    this.#titleListDragStartScrollLeft = list.scrollLeft;

    document.addEventListener('pointermove', this.#handleTitleListDragMove);
    document.addEventListener('pointerup', this.#handleTitleListDragEnd);
    document.addEventListener('pointercancel', this.#handleTitleListDragEnd);
  };

  /**
   * @param {PointerEvent} event
   */
  #handleTitleListDragMove = (event) => {
    const list = this.#titleListElement;
    if (!list || !this.#isDraggingTitleList) return;

    const deltaX = event.pageX - this.#titleListDragStartX;

    if (!this.#didDragTitleList) {
      if (Math.abs(deltaX) <= 5) return;

      this.#didDragTitleList = true;
      list.classList.add('is-dragging');
    }

    list.scrollLeft = this.#titleListDragStartScrollLeft - deltaX;
    event.preventDefault();
  };

  #handleTitleListDragEnd = () => {
    const list = this.#titleListElement;

    this.#isDraggingTitleList = false;
    list?.classList.remove('is-dragging');
    document.removeEventListener('pointermove', this.#handleTitleListDragMove);
    document.removeEventListener('pointerup', this.#handleTitleListDragEnd);
    document.removeEventListener('pointercancel', this.#handleTitleListDragEnd);

    if (this.#didDragTitleList) {
      setTimeout(() => {
        this.#didDragTitleList = false;
      }, 0);
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #handleTitleListClick = (event) => {
    if (!this.#didDragTitleList) return;
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(DSTREZZED_TITLE_LIST_SELECTOR)) return;

    event.preventDefault();
    event.stopPropagation();
    this.#didDragTitleList = false;
  };

  #teardownTitleListDrag() {
    this.#isDraggingTitleList = false;
    this.#didDragTitleList = false;
    this.#titleListElement?.classList.remove('is-dragging');
    this.#titleListElement = null;
    document.removeEventListener('pointermove', this.#handleTitleListDragMove);
    document.removeEventListener('pointerup', this.#handleTitleListDragEnd);
    document.removeEventListener('pointercancel', this.#handleTitleListDragEnd);
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#isDstrezzed && !this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list',
      ),
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          searchUrl.searchParams.set('type', 'product');
          searchUrl.searchParams.set('options[prefix]', 'last');
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Stores collections/pages carousels from the hidden Search block template.
   */
  #cacheEmptyStateCollectionsPages() {
    const { emptyStateCollectionsPages } = this.refs;

    if (!(emptyStateCollectionsPages instanceof HTMLElement)) return;

    const markup = emptyStateCollectionsPages.innerHTML.trim();

    if (!markup) return;

    this.#emptyStateCollectionsPagesMarkup = markup;
  }

  /**
   * Appends block-configured collections and pages below the no-results message.
   */
  #appendEmptyStateCollectionsPages() {
    if (!this.#emptyStateCollectionsPagesMarkup) return;

    const inner = this.refs.predictiveSearchResults.querySelector(
      '.predictive-search-results__inner[data-search-results]',
    );

    if (!inner?.querySelector('p.predictive-search-results__no-results')) return;

    for (const existing of inner.querySelectorAll('[data-empty-state-fallback]')) {
      existing.remove();
    }

    const template = document.createElement('template');
    template.innerHTML = this.#emptyStateCollectionsPagesMarkup;
    const fallback = template.content.firstElementChild;

    if (!fallback) return;

    inner.appendChild(fallback);
  }

  /**
   * Replaces the no-results copy with the Search block template after section morph.
   * @param {string} searchTerm - The term that returned no results
   */
  #applyNoResultsText(searchTerm) {
    const template = this.dataset.noResultsText;

    if (!template) return;

    const message = template.replaceAll('{query}', searchTerm);
    const noResultsEl = this.refs.predictiveSearchResults.querySelector('p.predictive-search-results__no-results');

    if (noResultsEl) {
      noResultsEl.textContent = message;
    }

    const statusEl = this.refs.predictiveSearchResults.querySelector('[role="status"]');

    if (statusEl) {
      statusEl.textContent = message;
    }
  }

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Replaces the results container so empty-state carousels and products are not left behind by morph.
   * @param {string} resultsMarkup - Section rendering API HTML
   */
  #replacePredictiveSearchResults(resultsMarkup) {
    const parsed = new DOMParser().parseFromString(resultsMarkup, 'text/html');
    const newResults =
      parsed.querySelector('#predictive-search-results') ??
      parsed.querySelector('.shopify-section')?.querySelector('#predictive-search-results');

    const { predictiveSearchResults } = this.refs;
    const currentResults = predictiveSearchResults.querySelector('#predictive-search-results');

    if (newResults instanceof HTMLElement && currentResults instanceof HTMLElement) {
      currentResults.replaceWith(document.importNode(newResults, true));
      return;
    }

    morph(predictiveSearchResults, resultsMarkup);
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product,query');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[limit]', this.#isDstrezzed ? '10' : '4');

    if (this.dataset.searchStyle) {
      url.searchParams.set('search_style', this.dataset.searchStyle);
    }

    const abortController = this.#createAbortController();
    this.#renderedSearchTerm = null;

    if (this.#isDstrezzed) {
      this.#fetchSearchProductCount(searchTerm, abortController);
    }

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        this.#replacePredictiveSearchResults(resultsMarkup);
        this.#renderedSearchTerm = searchTerm;
        // Apply products before count so a poorer /search grid can clear a stale count pending.
        this.#applyPendingDstrezzedProducts(searchTerm);
        this.#applyPendingProductCount(searchTerm);

        this.#applyNoResultsText(searchTerm);
        this.#appendEmptyStateCollectionsPages();
        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    if (this.dataset.searchStyle) {
      url.searchParams.set('search_style', this.dataset.searchStyle);
    }

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Fetch storefront search total + top products via the header Search block.
   * Uses `/search` (same matching as the results page) and nested `_product-card` markup.
   * Predictive search alone is too strict for multi-word queries and has no results_count.
   * Prefer `/search` products only when they are at least as rich as predictive cards —
   * short prefixes like "bird" match more titles in predictive than in storefront search.
   * @param {string} searchTerm
   * @param {AbortController} abortController
   */
  async #fetchSearchProductCount(searchTerm, abortController) {
    const sectionId = this.dataset.sectionId;

    if (!sectionId) return;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');

    try {
      const markup = await sectionRenderer.getSectionHTML(sectionId, false, url);

      if (abortController.signal.aborted) return;

      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const bootstrap = parsed.getElementById(DSTREZZED_SEARCH_BOOTSTRAP_ID);

      if (!(bootstrap instanceof HTMLElement)) return;

      const label = bootstrap.querySelector('#PredictiveSearchProductsCount')?.textContent?.trim();
      const products = bootstrap.querySelector(DSTREZZED_PRODUCTS_SELECTOR);

      if (!(products instanceof HTMLElement) || products.childElementCount === 0) return;

      products.hidden = false;

      const applied = this.#setDstrezzedProductGrid(searchTerm, products.outerHTML);

      if (applied && label) {
        this.#setProductCountTitle(searchTerm, label);
      }
    } catch {
      return;
    }
  }

  /**
   * @param {string} searchTerm
   * @param {string} label
   */
  #setProductCountTitle(searchTerm, label) {
    if (this.refs.searchInput.value.trim() !== searchTerm) return;

    this.#pendingProductCount = { term: searchTerm, label };

    if (this.#renderedSearchTerm !== searchTerm) return;

    const title = this.refs.predictiveSearchResults.querySelector(PRODUCT_COUNT_TITLE_SELECTOR);

    if (!(title instanceof HTMLElement)) return;

    title.textContent = label;
  }

  /**
   * @param {string} searchTerm
   */
  #applyPendingProductCount(searchTerm) {
    const pending = this.#pendingProductCount;

    if (!pending || pending.term !== searchTerm) return;

    this.#setProductCountTitle(pending.term, pending.label);
  }

  /**
   * @param {string} searchTerm
   * @param {string} markup
   * @returns {boolean} Whether the search grid was applied or queued
   */
  #setDstrezzedProductGrid(searchTerm, markup) {
    if (this.refs.searchInput.value.trim() !== searchTerm) return false;

    if (this.#renderedSearchTerm !== searchTerm) {
      this.#pendingDstrezzedProducts = { term: searchTerm, markup };
      return true;
    }

    const current = this.refs.predictiveSearchResults.querySelector(DSTREZZED_PRODUCTS_SELECTOR);

    if (!(current instanceof HTMLElement)) return false;

    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const next = parsed.querySelector(DSTREZZED_PRODUCTS_SELECTOR);

    if (!(next instanceof HTMLElement) || next.childElementCount === 0) return false;

    // Keep predictive cards when they already show more matches (e.g. "bird").
    if (next.childElementCount < current.childElementCount) {
      this.#pendingDstrezzedProducts = null;
      this.#pendingProductCount = null;
      return false;
    }

    next.hidden = false;
    current.replaceWith(document.importNode(next, true));
    this.#pendingDstrezzedProducts = null;
    return true;
  }

  /**
   * @param {string} searchTerm
   */
  #applyPendingDstrezzedProducts(searchTerm) {
    const pending = this.#pendingDstrezzedProducts;

    if (!pending || pending.term !== searchTerm) return;

    this.#setDstrezzedProductGrid(pending.term, pending.markup);
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;

    this.#currentIndex = -1;
    this.#pendingProductCount = null;
    this.#pendingDstrezzedProducts = null;
    this.#renderedSearchTerm = null;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    let parsedEmptySectionMarkup = null;

    if (this.#emptyStateMarkup) {
      parsedEmptySectionMarkup = new DOMParser()
        .parseFromString(`<div class="predictive-search-empty-section">${this.#emptyStateMarkup}</div>`, 'text/html')
        .querySelector('.predictive-search-empty-section');
    }

    if (!parsedEmptySectionMarkup) {
      const emptySectionMarkup = await sectionRenderer.getSectionHTML('predictive-search-empty', false, url);
      parsedEmptySectionMarkup = new DOMParser()
        .parseFromString(emptySectionMarkup, 'text/html')
        .querySelector('.predictive-search-empty-section');
    }

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (!this.#isDstrezzed && viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
